import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AttentionItem } from '../../domain/types';
import { useZunelo } from '../../context/ZuneloContext';
import { markdownToSlackMrkdwn } from '../../domain/markdownToSlackMrkdwn';
import { SlackCardExpanded } from './SlackCardExpanded';
import { SlackCardHeader } from './SlackCardHeader';
import { SlackDetailModal } from './SlackDetailModal';
import { useDetailSurfaceParam } from '../detail/useDetailSurfaceParam';
import {
  slackAccentStripBackground,
  slackArticleBackground,
  slackArticleShadow,
  slackCardHighlighted,
} from './slackCardVisuals';
import { useWorkContextMembership } from '../../hooks/useWorkContextMembership';
import { usePeopleMentionCandidates } from '../../hooks/usePeopleMentionCandidates';
import { openSettingsSection } from '../settings/settingsDeepLink';
import { useRightWorkspaceSidebar } from '../layout/RightWorkspaceSidebar';

interface SlackCardProps {
  item: AttentionItem;
  isSelected?: boolean;
  isMergeSelected?: boolean;
}

/** Prefer context updates without dropping richer prop transcript/title during SWR paint. */
export function mergeSlackCardItem(
  itemProp: AttentionItem,
  fromCtx: AttentionItem | undefined,
): AttentionItem {
  if (!fromCtx || fromCtx.id !== itemProp.id) return itemProp;
  const propMessages = itemProp.messages?.length ?? 0;
  const ctxMessages = fromCtx.messages?.length ?? 0;
  const contextIsRicher = ctxMessages > propMessages;
  return {
    ...itemProp,
    ...fromCtx,
    // Keep caller-provided copy until a richer hydrated transcript arrives.
    title: contextIsRicher ? fromCtx.title : itemProp.title,
    secondary: contextIsRicher ? fromCtx.secondary : itemProp.secondary,
    ai: fromCtx.ai ?? itemProp.ai,
    next: fromCtx.next ?? itemProp.next,
    messages: contextIsRicher ? fromCtx.messages : itemProp.messages,
  };
}

export const SlackCard: React.FC<SlackCardProps> = ({
  item: itemProp,
  isSelected = false,
  isMergeSelected = false,
}) => {
  const {
    selectItem,
    openContextSurface,
    isItemExpanded,
    toggleItemExpanded,
    isThreadSummaryOpen,
    toggleThreadSummary,
    sendThreadReply,
    generateThreadSummary,
    generateThreadDraft,
    openThreadChat,
    mergeSet,
    toggleMergeThread,
    navigateToItem,
    getJiraItemByKey,
    getItemById,
    errors,
    navigateToContext,
  } = useZunelo();

  const item = mergeSlackCardItem(itemProp, getItemById(itemProp.id));
  const isExpanded = isItemExpanded(item.id);
  const peopleMentionState = usePeopleMentionCandidates('slack', isExpanded);
  const legacyMentionCandidates = useMemo(
    () =>
      (item.members ?? []).map((member) => ({
        id: member.id,
        label: member.realName || member.name,
      })),
    [item.members],
  );
  const mentionCandidates =
    peopleMentionState.candidates.length > 0
      ? peopleMentionState.candidates
      : legacyMentionCandidates;
  const workContext = useWorkContextMembership(item.id);
  const { restore: restoreRightSidebar } = useRightWorkspaceSidebar();
  const summaryOpen = isThreadSummaryOpen(item.id);
  const isInMerge = mergeSet.includes(item.id);
  const sendError = errors.slack;
  const highlighted = slackCardHighlighted(isSelected, isMergeSelected, isInMerge);

  const [isDetailOpen, setIsDetailOpen] = useDetailSurfaceParam(item.id);
  const [replyDraft, setReplyDraft] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const expandedRootRef = useRef<HTMLDivElement | null>(null);

  const handleCardClick = () => {
    selectItem(item.id);
    toggleItemExpanded(item.id);
  };

  const handleToggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleItemExpanded(item.id);
    selectItem(item.id);
  };

  const scrollToThreadEnd = () => {
    const node = threadEndRef.current;
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  useEffect(() => {
    if (!isExpanded) return;
    const id = window.setTimeout(() => scrollToThreadEnd(), 50);
    return () => window.clearTimeout(id);
  }, [isExpanded, item.messages?.length]);

  const handleGenerateDraft = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDrafting) return;
    const idea = replyDraft.trim();
    if (!idea) return;
    setIsDrafting(true);
    try {
      const generated = await generateThreadDraft(item.id, idea);
      if (generated) setReplyDraft(generated);
    } finally {
      setIsDrafting(false);
    }
  };

  const sendReply = async () => {
    const trimmed = replyDraft.trim();
    if (!trimmed || isSending) return;
    setIsSending(true);
    try {
      const payload = markdownToSlackMrkdwn(trimmed).trim() || trimmed;
      const ok = await sendThreadReply(item.id, payload);
      if (ok) {
        setReplyDraft('');
        window.setTimeout(() => scrollToThreadEnd(), 80);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleAskAboutThread = (e: React.MouseEvent) => {
    e.stopPropagation();
    openThreadChat({
      itemId: item.id,
      channelName: item.primary,
      title: item.title,
      summary: item.ai,
    });
  };

  const handleToggleSummary = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (summaryOpen) toggleThreadSummary(item.id);
    else void generateThreadSummary(item.id);
  };

  const navigateToJira = (key: string) => {
    const linked = getJiraItemByKey(key);
    if (linked) navigateToItem('jira', linked.id);
  };

  const slackDetailProps = {
    summaryOpen,
    isInMerge,
    replyDraft,
    isSending,
    isDrafting,
    sendError,
    expandedRootRef,
    threadEndRef,
    onScrollToEnd: scrollToThreadEnd,
    onToggleMerge: (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleMergeThread(item.id);
    },
    onToggleSummary: handleToggleSummary,
    onNavigateToJira: navigateToJira,
    isJiraRefResolved: (key: string) => Boolean(getJiraItemByKey(key)),
    onReplyChange: setReplyDraft,
    onSubmitReply: () => {
      void sendReply();
    },
    onAskAboutThread: handleAskAboutThread,
    onGenerateDraft: handleGenerateDraft,
    mentionCandidates,
    onMentionCandidateUnavailable: () => openSettingsSection('people'),
    onSend: (e: React.MouseEvent) => {
      e.stopPropagation();
      void sendReply();
    },
  };

  return (
    <>
      <article
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          padding: '15px 18px',
          borderRadius: 18,
          cursor: 'pointer',
          overflow: 'hidden',
          background: slackArticleBackground(highlighted),
          border: 'none',
          boxShadow: slackArticleShadow(highlighted),
          transition: 'all 0.2s ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            background: slackAccentStripBackground(item.st),
          }}
        />

        <SlackCardHeader
          item={item}
          isExpanded={isExpanded}
          workContext={workContext}
          onCardClick={handleCardClick}
          onToggleExpanded={handleToggleExpanded}
          onNavigateToContext={() => workContext && navigateToContext(workContext.id)}
          onOpenContext={() => {
            openContextSurface(item.id);
            restoreRightSidebar();
          }}
          onOpenDetail={() => setIsDetailOpen(true)}
          permalink={item.rawSlackThread?.permalink}
        />

        {isExpanded ? <SlackCardExpanded item={item} {...slackDetailProps} /> : null}
      </article>

      {isDetailOpen ? (
        <SlackDetailModal
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          item={item}
          {...slackDetailProps}
        />
      ) : null}
    </>
  );
};
